// ============================================================================
// ACTIVITY SERVICE
// ============================================================================
// Business logic and HTTP calls for the Activity master.
// Follows the same PagedResult → PaginatedResponse transform as engagementTypeService.

import { get, post, put, del } from '../client/axiosClient';
import { activityEndpoints } from '../endpoints/activity.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Activity,
  CreateActivityRequest,
  UpdateActivityRequest,
  ActivitySearchRequest,
} from '../types';

class ActivityService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: ActivitySearchRequest
  ): Promise<PaginatedResponse<Activity>> {
    const endpoint = params
      ? buildEndpointWithQuery(activityEndpoints.basePath(), params as Record<string, any>)
      : activityEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Activity>> {
    const response = await get<ApiResponse<Activity>>(
      activityEndpoints.getById(id)
    );
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Activity>> {
    const response = await get<ApiResponse<Activity>>(
      activityEndpoints.getByCode(code)
    );
    return response.data;
  }

  async create(
    data: CreateActivityRequest
  ): Promise<ApiResponse<Activity>> {
    const response = await post<ApiResponse<Activity>>(
      activityEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateActivityRequest
  ): Promise<ApiResponse<Activity>> {
    const response = await put<ApiResponse<Activity>>(
      activityEndpoints.update(id),
      data
    );
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(activityEndpoints.delete(id));
    return { success: true };
  }
}

export const activityService = new ActivityService();
export default activityService;
